import { BookOpen, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Section } from "./SortableRow";
import { buildSectionLabels } from "./sectionLabels";

interface SectionFilterBarProps {
  sections: Section[];
  selectedSectionId: string | null;
  onSelect: (id: string | null) => void;
}

const ALL_SECTIONS_VALUE = "__all__";

export const SectionFilterBar = ({ sections, selectedSectionId, onSelect }: SectionFilterBarProps) => {
  const labels = buildSectionLabels(sections);

  const currentIndex = selectedSectionId !== null
    ? sections.findIndex((s) => s.id === selectedSectionId)
    : -1;

  const canGoUp = currentIndex > 0;
  const canGoDown = currentIndex !== -1 && currentIndex < sections.length - 1;

  const handleValueChange = (value: string) => {
    onSelect(value === ALL_SECTIONS_VALUE ? null : value);
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 mb-4 bg-[#F7F6EE]">
      <div className="flex items-center gap-3 shrink-0">
        <BookOpen className="w-5 h-5 text-muted-foreground shrink-0" />
        <span className="text-sm font-bold shrink-0">
          Select section to view:
        </span>
      </div>
      <div className="flex items-center gap-3 grow">
        <Select
          value={selectedSectionId ?? ALL_SECTIONS_VALUE}
          onValueChange={handleValueChange}
        >
          <SelectTrigger className="w-full max-w-2xl rounded-none">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_SECTIONS_VALUE}>All Sections</SelectItem>
            {sections.map((section) => {
              const label = labels.get(section.id) ?? "";
              const isChild = section.parent_id !== null;
              return (
                <SelectItem key={section.id} value={section.id}>
                  <span className={isChild ? "pl-4" : undefined}>
                    {label} {section.name}
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {selectedSectionId !== null && (
          <>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0"
              disabled={!canGoUp}
              onClick={() => canGoUp && onSelect(sections[currentIndex - 1].id)}
              aria-label="Previous section"
            >
              <ChevronUp className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0"
              disabled={!canGoDown}
              onClick={() => canGoDown && onSelect(sections[currentIndex + 1].id)}
              aria-label="Next section"
            >
              <ChevronDown className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
